import { QueryClient } from '@tanstack/react-query';
import { ApiError } from './apiClient';
import { useAuthStore } from '@/stores/authStore';

// Type guard to safely extract message from error body
function getErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    // Safely access the body property with type checking
    const body = error.body as unknown
    if (
      body !== null &&
      typeof body === 'object' &&
      'message' in body &&
      typeof (body as Record<string, unknown>).message === 'string'
    ) {
      return String((body as Record<string, unknown>).message)
    }
    return error.message
  }
  if (error instanceof Error) {
    return error.message
  }
  return 'An error occurred'
}

// Handle global 401 errors
const handle401Error = () => {
  const { clearAuth } = useAuthStore.getState();
  clearAuth(); // This already handles preserving encryption settings
  
  // Note: We don't need to remove token/user manually since clearAuth() handles it
  // and preserves encryption settings
  
  // Redirect to login if not already there
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

// Create a client
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        // Don't retry on authentication errors
        if (error instanceof ApiError && error.status === 401) {
          handle401Error();
          return false;
        }
        // Never retry on server errors (e.g., 5xx) per product requirement
        if (error instanceof ApiError && error.status >= 500) {
          return false;
        }
        // Otherwise allow up to 2 retries for transient cases (e.g., network hiccup)
        return failureCount < 2;
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    },
    mutations: {
      onError: (error) => {
        // Global error handling for mutations
        if (error instanceof ApiError) {
          if (error.status === 401) {
            handle401Error();
            return;
          }
          const message = getErrorMessage(error);
          console.error('API Error:', message);
        } else {
          console.error('Unexpected error:', error);
        }
      },
    },
  },
});

